/* ==========================================================================
   ORLAVEN - Contact page behaviour
   --------------------------------------------------------------------------
   BUGFIX (root cause): sections/orlaven-contact.liquid referenced this file on
   line 2, but the asset did not exist on ANY theme -> HTTP 404. With no JS the
   Subject select stayed permanently disabled ("Select a topic first"), every
   conditional group stayed collapsed/disabled, and no client validation ran,
   so the contact form was effectively unusable.

   Responsibilities (all hooks are already present in the Liquid markup):
     data-orlaven-contact           section root
     data-contact-topic             topic <select>
     data-contact-subject           subject <select>  (populated from topic)
     data-contact-group="<key>"     conditional fieldsets
     data-contact-context           contextual hint panel
     data-contact-order-number      order field (required for order topics)
     data-required-when-visible     required only while its group is shown
     data-min-length                minimum length for the message textarea
     data-contact-attachment        file input (+ -name mirror, -note helper)
     data-contact-errors            error summary
     data-contact-live              aria-live region
     data-contact-submit            submit button
     data-contact-preset            quick-topic buttons
     data-reveal                    scroll reveal targets
   ========================================================================== */

(() => {
  'use strict';

  const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

  /* Topic -> { subjects, group, context }
     `group` selects which data-contact-group fieldset to reveal.           */
  const TOPICS = {
    'Order & Tracking': {
      group: 'order',
      subjects: ['Track My Order', 'Order Not Received', 'Delayed Shipment', 'Wrong Item Received', 'Missing Item'],
      context: 'Please include your order number so we can locate your shipment immediately.'
    },
    'Order Changes & Cancellation': {
      group: 'order',
      subjects: ['Change Shipping Address', 'Change Size or Colour', 'Cancel My Order', 'Combine Two Orders'],
      context: 'Changes are only possible before your order enters production. Include your order number.'
    },
    'Returns, Exchanges & Refunds': {
      group: 'order',
      subjects: ['Start a Return', 'Exchange Size', 'Refund Status', 'Return Authorization', 'Damaged on Arrival'],
      context: 'Do not ship anything back before you receive a return authorization from our team.'
    },
    'Size & Fit': {
      group: 'size',
      subjects: ['Help Choosing My Size', 'Between Two Sizes', 'Sleeve or Length Question', 'Fit Advice for My Build'],
      context: 'Share your height, weight and chest measurement for an accurate recommendation.'
    },
    'Product Information': {
      group: 'product',
      subjects: ['Leather Type & Quality', 'Lining & Hardware', 'Care Instructions', 'Stock Availability', 'Restock Date'],
      context: 'Include the product name or URL so we can answer about the exact item.'
    },
    'Customization & Bespoke Orders': {
      group: 'custom',
      subjects: ['Custom Jacket Inquiry', 'Bespoke Measurements', 'Custom Artwork or Embroidery', 'Colour or Leather Change'],
      context: 'Tell us the jacket style, leather, colour, budget and any deadline you need to meet.'
    },
    'Shipping & Delivery': {
      group: 'order',
      subjects: ['Delivery Timeframe', 'Shipping Cost', 'Customs & Duties', 'Change Delivery Date'],
      context: 'If this relates to an existing order, please include the order number.'
    },
    'Payment & Billing': {
      group: 'order',
      subjects: ['Payment Failed', 'Double Charge', 'Invoice Request', 'Payment Methods'],
      context: 'Never send full card details. Share only your order number and the error you saw.'
    },
    'Website & Technical Support': {
      group: 'technical',
      subjects: ['Checkout Error', 'Account Login Issue', 'Discount Code Not Working', 'Page Not Loading'],
      context: 'Tell us your device and browser, and what you were doing when the problem happened.'
    },
    'Wholesale, Corporate & Bulk Orders': {
      group: 'wholesale',
      subjects: ['Wholesale Pricing', 'Corporate Gifting', 'Bulk Order Quote', 'Become a Stockist'],
      context: 'Include your company name, country and the estimated quantity you require.'
    },
    'Press, Influencer & Partnerships': {
      group: 'press',
      subjects: ['Press Inquiry', 'Influencer Collaboration', 'Affiliate Program', 'Brand Partnership'],
      context: 'Please add your website or social profile so we can review your audience.'
    },
    'General Inquiry': {
      group: 'other',
      subjects: ['General Question', 'Feedback', 'Something Else'],
      context: ''
    }
  };

  const init = (root) => {
    if (root.dataset.orlavenContactReady === 'true') return;
    root.dataset.orlavenContactReady = 'true';

    const form = root.querySelector('form');
    const topicEl = root.querySelector('[data-contact-topic]');
    const subjectEl = root.querySelector('[data-contact-subject]');
    const contextEl = root.querySelector('[data-contact-context]');
    const groups = Array.from(root.querySelectorAll('[data-contact-group]'));
    const errorsEl = root.querySelector('[data-contact-errors]');
    const liveEl = root.querySelector('[data-contact-live]');
    const submitEl = root.querySelector('[data-contact-submit]');

    /* ---------- conditional groups ---------- */

    const setGroupState = (group, visible) => {
      group.classList.toggle('is-visible', visible);
      group.querySelectorAll('input, select, textarea').forEach((field) => {
        // Disabled fields are not submitted - exactly what we want for
        // groups that do not apply to the chosen topic.
        field.disabled = !visible;
        if (field.dataset.requiredWhenVisible === 'true') {
          field.required = visible;
        }
        if (!visible) clearFieldError(field);
      });
    };

    const showGroup = (key) => {
      groups.forEach((g) => setGroupState(g, g.dataset.contactGroup === key));
    };

    /* ---------- subject select ---------- */

    const populateSubjects = (topic) => {
      const conf = TOPICS[topic];
      const i18n = window.ORLAVEN_CONTACT_I18N || {};
      const subjectsMap = i18n.subjects || {};
      subjectEl.innerHTML = '';

      if (!conf) {
        subjectEl.appendChild(new Option(i18n.select_subject_first || 'Select a topic first', '', true, true));
        subjectEl.options[0].disabled = true;
        subjectEl.disabled = true;
        return;
      }

      subjectEl.appendChild(new Option(i18n.select_subject || 'Select a subject', '', true, true));
      subjectEl.options[0].disabled = true;
      conf.subjects.forEach((s) => {
        const label = subjectsMap[s] || s;
        subjectEl.appendChild(new Option(label, s));
      });
      subjectEl.disabled = false;
    };

    const applyTopic = (topic, { silent = false } = {}) => {
      const conf = TOPICS[topic];
      populateSubjects(topic);
      showGroup(conf ? conf.group : null);

      if (contextEl) {
        const i18n = window.ORLAVEN_CONTACT_I18N || {};
        const contextsMap = i18n.contexts || {};
        const text = contextsMap[topic] || (conf && conf.context ? conf.context : '');
        contextEl.textContent = text;
        contextEl.classList.toggle('is-visible', Boolean(text));
      }

      // Order number is required for the order-related topics.
      const orderEl = root.querySelector('[data-contact-order-number]');
      if (orderEl && conf && conf.group === 'order') {
        orderEl.required = true;
      }

      if (!silent) {
        // Changing the topic swaps which fields apply, so any previous error
        // summary / field messages are stale - clear them.
        if (errorsEl) {
          errorsEl.classList.remove('is-visible');
          errorsEl.innerHTML = '';
        }
        root.querySelectorAll('[aria-invalid="true"]').forEach(clearFieldError);

        if (liveEl && conf) {
          liveEl.textContent = `Topic set to ${topic}. Please choose a subject.`;
        }
      }
    };

    if (topicEl && subjectEl) {
      topicEl.addEventListener('change', () => applyTopic(topicEl.value));
      // Restore state after a failed server-side submit re-render.
      applyTopic(topicEl.value, { silent: true });
    }

    /* ---------- quick topic presets ---------- */

    root.querySelectorAll('[data-contact-preset]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const { topic, subject } = btn.dataset;
        if (!topicEl || !TOPICS[topic]) return;
        topicEl.value = topic;
        applyTopic(topic);
        if (subject) subjectEl.value = subject;
        (subjectEl || topicEl).focus();
        root.querySelector('.orlaven-contact__form-card')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    /* ---------- attachment ---------- */

    const fileEl = root.querySelector('[data-contact-attachment]');
    if (fileEl) {
      const noteEl = root.querySelector('[data-contact-attachment-note]');
      const mirrorEl = root.querySelector('[data-contact-attachment-name]');
      const defaultNote = noteEl ? noteEl.textContent : '';

      fileEl.addEventListener('change', () => {
        const file = fileEl.files && fileEl.files[0];
        if (!file) {
          if (noteEl) {
            noteEl.textContent = defaultNote;
            noteEl.classList.remove('orlaven-contact__helper--warning');
          }
          if (mirrorEl) { mirrorEl.value = ''; mirrorEl.disabled = true; }
          return;
        }

        if (file.size > MAX_ATTACHMENT_BYTES) {
          fileEl.value = '';
          if (noteEl) {
            noteEl.textContent = 'That file is larger than 8MB. Please attach a smaller file.';
            noteEl.classList.add('orlaven-contact__helper--warning');
          }
          if (mirrorEl) { mirrorEl.value = ''; mirrorEl.disabled = true; }
          return;
        }

        const kb = Math.round(file.size / 1024);
        if (noteEl) {
          noteEl.textContent = `Attached: ${file.name} (${kb} KB)`;
          noteEl.classList.remove('orlaven-contact__helper--warning');
        }
        // Shopify contact forms cannot carry the binary, so we submit the
        // filename and our team requests the file by email.
        if (mirrorEl) {
          mirrorEl.value = `${file.name} (${kb} KB)`;
          mirrorEl.disabled = false;
        }
      });
    }

    /* ---------- validation ---------- */

    function fieldErrorEl(field) {
      const wrap = field.closest('.orlaven-contact__field');
      return wrap ? wrap.querySelector('.orlaven-contact__error') : null;
    }

    function clearFieldError(field) {
      field.removeAttribute('aria-invalid');
      const el = fieldErrorEl(field);
      if (el) el.textContent = '';
    }

    function setFieldError(field, message) {
      field.setAttribute('aria-invalid', 'true');
      const el = fieldErrorEl(field);
      if (el) el.textContent = message;
    }

    function labelFor(field) {
      const wrap = field.closest('.orlaven-contact__field');
      const label = wrap ? wrap.querySelector('label') : null;
      return label ? label.textContent.replace('*', '').trim() : 'This field';
    }

    function validateField(field) {
      if (field.disabled) return null;
      clearFieldError(field);

      const value = (field.value || '').trim();

      if (field.required && !value) {
        const i18n = window.ORLAVEN_CONTACT_I18N || {};
        if (i18n.field_required) {
          return `${labelFor(field)} ${i18n.field_required}`;
        }
        return `${labelFor(field)} is required.`;
      }
      if (value && field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
        return 'Please enter a valid email address.';
      }
      if (value && field.type === 'url' && !/^https?:\/\/.+/i.test(value)) {
        return 'Please include the full URL, starting with https://';
      }
      const min = parseInt(field.dataset.minLength || '', 10);
      if (!Number.isNaN(min) && value && value.length < min) {
        return `Please provide at least ${min} characters.`;
      }
      return null;
    }

    const fields = () => Array.from(root.querySelectorAll('input, select, textarea'))
      .filter((f) => f.type !== 'hidden' && f.type !== 'file' && !f.disabled);

    fields().forEach((f) => {
      f.addEventListener('blur', () => {
        const msg = validateField(f);
        if (msg) setFieldError(f, msg); else clearFieldError(f);
      });
      f.addEventListener('input', () => {
        if (f.getAttribute('aria-invalid') === 'true') clearFieldError(f);
      });
    });

    if (form) {
      // Take over validation so we can show the grouped error summary and
      // per-field messages. Set from JS (not in the markup) so that if this
      // script ever fails to load, the browser's own required-field
      // validation still protects the form.
      form.noValidate = true;

      form.addEventListener('submit', (event) => {
        const problems = [];
        let firstBad = null;

        fields().forEach((f) => {
          const msg = validateField(f);
          if (msg) {
            setFieldError(f, msg);
            problems.push(msg);
            if (!firstBad) firstBad = f;
          }
        });

        if (problems.length) {
          event.preventDefault();
          if (errorsEl) {
            errorsEl.innerHTML =
              '<strong>Please review the highlighted fields:</strong><ul>' +
              problems.map((p) => `<li>${p}</li>`).join('') +
              '</ul>';
            errorsEl.classList.add('is-visible');
            errorsEl.focus?.();
          }
          if (liveEl) {
            liveEl.textContent = `${problems.length} field${problems.length > 1 ? 's' : ''} need attention.`;
          }
          firstBad?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        if (errorsEl) {
          errorsEl.classList.remove('is-visible');
          errorsEl.innerHTML = '';
        }
        if (submitEl) {
          submitEl.disabled = true;
          submitEl.dataset.originalLabel = submitEl.textContent.trim();
          const i18n = window.ORLAVEN_CONTACT_I18N || {};
          submitEl.textContent = i18n.sending || 'SENDING…';
        }
      });
    }

    /* ---------- scroll reveal (progressive enhancement) ---------- */

    const reveals = Array.from(root.querySelectorAll('[data-reveal]'));
    if (!reveals.length) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      reveals.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const delay = (parseInt(el.dataset.reveal || '1', 10) - 1) * 90;
        setTimeout(() => el.classList.add('is-visible'), Math.max(0, delay));
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    reveals.forEach((el) => io.observe(el));

    // Failsafe: if anything above goes wrong, never leave content invisible.
    setTimeout(() => reveals.forEach((el) => el.classList.add('is-visible')), 2600);
  };

  const boot = () => document.querySelectorAll('[data-orlaven-contact]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Shopify theme editor re-renders sections without a page reload.
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll?.('[data-orlaven-contact]').forEach(init);
  });
})();
